// File: SuggestedTradesForItem.js
// ✅ Drop-in replacement (aligned to TradeDetail owner-tools requirement)
// - Enforces SINGLE-card carousel (1 card visible at a time)
// - Keeps your scoring, filtering, PublicTrades-first behavior, and premium UI hooks

import { db } from "../firebase";
import { collection, getDocs, doc, setDoc, query, where, limit } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./SuggestedTradesForItem.css";

/* -------------------- HELPERS -------------------- */
const normStr = (v) => String(v ?? "").trim();
const safeLower = (v) => String(v ?? "").trim().toLowerCase();

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const normalizeImages = (t) => {
  const val = t?.imageURLs ?? t?.imageUrls ?? t?.images ?? t?.imageURL ?? t?.imageUrl;
  const arr = Array.isArray(val) ? val : typeof val === "string" ? [val] : [];
  return arr.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean).slice(0, 10);
};

const isProbablyAvailable = (t) => {
  if (!t) return false;
  if (t.isAvailable === false) return false;
  const st = safeLower(t.status);
  if (["closed", "sold", "cancelled", "canceled", "completed", "expired"].includes(st)) return false;
  return true;
};

const getOwnerUid = (t) => normStr(t?.ownerId || t?.ownerUid || t?.sellerUid || "");

const titleTokens = (s) =>
  safeLower(s)
    .replace(/[^\w\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 8);

const tokenOverlap = (aTokens, bTokens) => {
  if (!aTokens.length || !bTokens.length) return 0;
  const setB = new Set(bTokens);
  let hit = 0;
  aTokens.forEach((t) => {
    if (setB.has(t)) hit += 1;
  });
  return hit;
};

const pickValue = (t) => {
  const v = t?.estimatedValue ?? t?.expectedCashAmount ?? t?.price ?? null;
  const num = Number(v);
  return Number.isFinite(num) ? num : null;
};

const normCategory = (t) => safeLower(t?.category || t?.itemCategory || "");
const normCondition = (t) => safeLower(t?.condition || "");

/* -------------------- COMPONENT -------------------- */
const SuggestedTradesForItem = ({ currentTrade, uid }) => {
  const navigate = useNavigate();

  const [suggestedTrades, setSuggestedTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("PublicTrades");
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ SINGLE card per page (critical requirement)
  const tradesPerPage = 1;
  const [currentPage, setCurrentPage] = useState(0);

  const startIndex = currentPage * tradesPerPage;
  const endIndex = startIndex + tradesPerPage;
  const currentTrades = suggestedTrades.slice(startIndex, endIndex);

  const canPrev = currentPage > 0;
  const canNext = (currentPage + 1) * tradesPerPage < suggestedTrades.length;

  const baseTradeValue = useMemo(() => pickValue(currentTrade), [currentTrade]);
  const baseCategory = useMemo(() => normCategory(currentTrade), [currentTrade]);
  const baseCondition = useMemo(() => normCondition(currentTrade), [currentTrade]);
  const baseTitleTokens = useMemo(() => titleTokens(currentTrade?.title || ""), [currentTrade?.title]);

  const myUid = useMemo(() => normStr(uid), [uid]);

  const postTrade = useCallback(() => {
    navigate("/market/post-trade");
  }, [navigate]);

  const buildSuggestions = useCallback(async () => {
    if (!currentTrade?.id || !myUid) return;
    if (!baseTradeValue && !baseCategory) return;

    setLoading(true);
    setErrorMsg("");

    try {
      const pubCol = collection(db, "PublicTrades");
      const resultsMap = new Map();

      const rawCategory = normStr(currentTrade?.category || currentTrade?.itemCategory || "");
      const catNorm = baseCategory;

      try {
        const qa = query(pubCol, where("categoryNorm", "==", catNorm), limit(40));
        const sa = await getDocs(qa);
        sa.docs.forEach((d) => resultsMap.set(d.id, { id: d.id, ...d.data() }));
      } catch {}

      if (rawCategory) {
        const qb = query(pubCol, where("category", "==", rawCategory), limit(40));
        const sb = await getDocs(qb);
        sb.docs.forEach((d) => resultsMap.set(d.id, { id: d.id, ...d.data() }));
      }

      let pool = Array.from(resultsMap.values());

      if (pool.length === 0) {
        const s = await getDocs(query(pubCol, limit(60)));
        pool = s.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      setSource("PublicTrades");

      if (pool.length === 0) {
        const snap = await getDocs(query(collection(db, "Trades"), limit(80)));
        pool = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSource("Trades");
      }

      const priceMin = baseTradeValue ? baseTradeValue * 0.8 : null;
      const priceMax = baseTradeValue ? baseTradeValue * 1.2 : null;

      const me = myUid;
      const baseLocCity = safeLower(currentTrade?.location?.city || "");
      const baseLocCountry = safeLower(currentTrade?.location?.country || "");

      const scored = pool
        .filter((t) => t?.id && t.id !== currentTrade.id)
        .filter((t) => {
          const owner = getOwnerUid(t);
          return !(owner && owner === me);
        })
        .filter(isProbablyAvailable)
        .map((t) => {
          const reasons = [];
          let score = 0;

          const cat = normCategory(t);
          const cond = normCondition(t);
          const v = pickValue(t);

          if (baseCategory && cat && cat === baseCategory) {
            score += 3;
            reasons.push("Same category");
          } else if (baseCategory && cat && (cat.includes(baseCategory) || baseCategory.includes(cat))) {
            score += 1;
            reasons.push("Related category");
          }

          if (baseCondition && cond && cond === baseCondition) {
            score += 1;
            reasons.push("Same condition");
          }

          if (priceMin != null && priceMax != null && v != null) {
            if (v >= priceMin && v <= priceMax) {
              score += 3;
              reasons.push("Similar value");
            } else {
              const dist = Math.abs(v - baseTradeValue) / baseTradeValue;
              if (dist <= 0.5) {
                score += 1;
                reasons.push("Close value");
              }
            }
          }

          const tok = titleTokens(t?.title || "");
          const overlap = tokenOverlap(baseTitleTokens, tok);
          if (overlap >= 2) {
            score += 2;
            reasons.push("Title relevance");
          } else if (overlap === 1) {
            score += 1;
            reasons.push("Some title relevance");
          }

          const city = safeLower(t?.location?.city || "");
          const country = safeLower(t?.location?.country || "");
          if (baseLocCity && city && city === baseLocCity) {
            score += 1;
            reasons.push("Same city");
          } else if (baseLocCountry && country && country === baseLocCountry) {
            score += 1;
            reasons.push("Same country");
          }

          return {
            ...t,
            imageURLs: normalizeImages(t),
            _score: score,
            _reasons: reasons.slice(0, 3),
            _value: v,
          };
        })
        .filter((t) => t._score >= 4)
        .sort((a, b) => {
          if (b._score !== a._score) return b._score - a._score;
          if (baseTradeValue && a._value != null && b._value != null) {
            const da = Math.abs(a._value - baseTradeValue);
            const dbb = Math.abs(b._value - baseTradeValue);
            if (da !== dbb) return da - dbb;
          }
          const am = a?.createdAt?.seconds ? a.createdAt.seconds : 0;
          const bm = b?.createdAt?.seconds ? b.createdAt.seconds : 0;
          return bm - am;
        })
        .slice(0, 10);

      setSuggestedTrades(scored);
      setCurrentPage(0);

      // light preference training (kept)
      try {
        const categoryCount = {};
        const styles = [];
        scored.forEach((match) => {
          const c = normStr(match.category || match.itemCategory || "");
          if (c) categoryCount[c] = (categoryCount[c] || 0) + 1;
          if (match._reasons.includes("Title relevance")) styles.push(match.title);
        });

        const topCategories = Object.entries(categoryCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([cat]) => cat);

        await setDoc(
          doc(db, "aiSuggestions", myUid),
          {
            suggestedPreferences: {
              favoriteCategories: topCategories,
              preferredStyles: styles.length ? styles[0] : "",
            },
            inferredFromBehavior: true,
            lastUpdated: new Date().toISOString(),
            source,
          },
          { merge: true }
        );
      } catch {}
    } catch (e) {
      console.error("Error fetching suggested trades:", e);
      setSuggestedTrades([]);
      setErrorMsg("Could not load suggestions right now.");
    } finally {
      setLoading(false);
    }
  }, [currentTrade, myUid, baseTradeValue, baseCategory, baseCondition, baseTitleTokens, source]);

  useEffect(() => {
    buildSuggestions();
  }, [buildSuggestions]);

  if (!currentTrade) return null;

  return (
    <div className="suggested-wrapper premium">
      <div className="suggested-head">
        <div>
          <div className="suggested-kicker">Owner tools</div>
          <h3 className="suggested-title">Suggested trades for this item</h3>
          <div className="suggested-subtitle">
            Curated matches based on category, condition, value, and relevance.
            <span className="suggested-source">Source: {source}</span>
          </div>
        </div>

        <div className="suggested-actions">
          <button
            type="button"
            className="suggested-btn ghost"
            onClick={buildSuggestions}
            disabled={loading}
            title="Refresh suggestions"
          >
            ⟳ Refresh
          </button>
          <button type="button" className="suggested-btn primary" onClick={postTrade}>
            ➕ Post a trade
          </button>
        </div>
      </div>

      {loading ? (
        <div className="suggested-scroll">
          <div className="suggested-card skeleton">
            <div className="sk-img" />
            <div className="sk-body">
              <div className="sk-line w80" />
              <div className="sk-line w55" />
              <div className="sk-line w65" />
            </div>
          </div>
        </div>
      ) : suggestedTrades.length === 0 ? (
        <div className="no-suggestions-message premium">
          <div className="empty-icon">💡</div>
          <div className="empty-title">No strong matches yet</div>
          <div className="empty-sub">
            Add more listings or ensure your item has category and value set to improve matching.
          </div>
          {errorMsg ? <div className="empty-error">{errorMsg}</div> : null}
          <div className="empty-cta">
            <button type="button" className="suggested-btn primary" onClick={postTrade}>
              ➕ Post a new trade
            </button>
            <button type="button" className="suggested-btn ghost" onClick={buildSuggestions}>
              ⟳ Try again
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="carousel-controls">
            <button
              className="carousel-arrow up"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
              disabled={!canPrev}
              type="button"
              aria-label="Previous suggestion"
              title="Previous"
            >
              ▲
            </button>
          </div>

          <div className="suggested-scroll">
            {currentTrades.map((trade) => {
              const img = trade.imageURLs?.[0] || "/default-placeholder.png";
              const value = trade._value ?? pickValue(trade) ?? 0;

              return (
                <div key={trade.id} className="suggested-card premium-card">
                  <Link to={`/trade/${trade.id}`} className="suggested-link">
                    <div className="suggested-media">
                      <img
                        src={img}
                        alt={trade.title || "Trade"}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/default-placeholder.png";
                        }}
                      />
                      <div className="suggested-badges">
                        <span className="badge score">Match {trade._score}/10</span>
                        {trade._reasons?.[0] ? <span className="badge reason">{trade._reasons[0]}</span> : null}
                      </div>
                    </div>

                    <div className="suggested-info">
                      <div className="suggested-name">{trade.title || "Untitled item"}</div>
                      <div className="suggested-meta">
                        <span className="meta-pill">{normStr(trade.category || trade.itemCategory || "Category")}</span>
                        {trade.condition ? <span className="meta-pill">{trade.condition}</span> : null}
                      </div>

                      <div className="suggested-priceRow">
                        <div className="suggested-price">{moneyZAR(value)}</div>
                        <div className="suggested-loc">
                          {trade.location?.city ? trade.location.city : "—"}
                          {trade.location?.country ? `, ${trade.location.country}` : ""}
                        </div>
                      </div>

                      {Array.isArray(trade._reasons) && trade._reasons.length > 1 ? (
                        <div className="suggested-reasons">
                          {trade._reasons.slice(1, 3).map((r) => (
                            <span key={r} className="reason-pill">
                              {r}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </Link>

                  <button
                    className="offer-btn premium-offer"
                    type="button"
                    onClick={() => navigate(`/trade/${trade.id}`)}
                    title="Open this suggested trade"
                  >
                    View suggestion →
                  </button>
                </div>
              );
            })}
          </div>

          <div className="carousel-controls">
            <button
              className="carousel-arrow down"
              onClick={() => setCurrentPage((p) => (canNext ? p + 1 : p))}
              disabled={!canNext}
              type="button"
              aria-label="Next suggestion"
              title="Next"
            >
              ▼
            </button>
          </div>

          <div className="suggested-footer">
            <div className="pager">
              Page {currentPage + 1} of {Math.max(1, Math.ceil(suggestedTrades.length / tradesPerPage))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SuggestedTradesForItem;
